import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Pagination — reusable controls for paginated admin tables.
 *
 * Props:
 *   - page, pageSize, total, totalPages, hasNext, hasPrev (from API meta)
 *   - onPageChange(nextPage)
 *   - testid (root data-testid)
 *
 * Renders nothing when `total <= pageSize` (per M916 spec — hide controls
 * when there's only one page worth of data).
 *
 * Layout: left side shows the human-readable "X–Y din Z rezultate"
 * counter; right side shows « Anterior · Pagina X din N · Următor ».
 */
export default function Pagination({
  page, pageSize, total, totalPages, hasNext, hasPrev, onPageChange,
  testid = "pagination",
}) {
  if (!total || total <= pageSize) return null;

  const startIdx = (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-2 py-3 text-sm text-[#373A45]"
      data-testid={testid}
    >
      <div className="text-[#6B7280]" data-testid={`${testid}-range`}>
        Se afișează <span className="font-semibold text-[#373A45]">{startIdx}–{endIdx}</span>{" "}
        din <span className="font-semibold text-[#373A45]">{total}</span> rezultate
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-white text-[#373A45] font-semibold text-xs hover:border-[#965EA7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`${testid}-prev`}
        >
          <ChevronLeft size={14} /> Anterior
        </button>
        <span className="text-[11px] uppercase tracking-widest text-[#6B7280] px-2" data-testid={`${testid}-info`}>
          Pagina <span className="font-bold text-[#373A45]">{page}</span> din{" "}
          <span className="font-bold text-[#373A45]">{totalPages}</span>
        </span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-white text-[#373A45] font-semibold text-xs hover:border-[#965EA7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`${testid}-next`}
        >
          Următor <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
